import { useEffect } from "react";
import { onValue, ref } from "firebase/database";
import { db } from "../firebaseConfig";
import { applyRemoteNavGrid, loadNavGrid } from "../utils/navGrid.js";
import { useAuthStore } from "../stores/useAuthStore";
import { useCan } from "./useCan.js";
import { hydrateNavGridFromPersistent } from "../utils/navGridPersistent.js";
import { fetchNavGridFromCloud } from "../utils/navGridCloudSync.js";

export default function useNavGridSync(options = {}) {
  const enabled = options?.enabled ?? true;
  const user = useAuthStore((s) => s.user);
  const canPlanRoute = useCan("PanelNav", "plan_route");
  const canManageNavGrid = useCan("PanelNavGridAdmin", "open");
  const canUsePremiumSegments = useCan(
    "PanelNavGridAccess",
    "use_premium_segments"
  );
  const canSync = canPlanRoute || canManageNavGrid || canUsePremiumSegments;

  useEffect(() => {
    if (!enabled) return;
    if (!user || !canSync) return;

    let active = true;
    const syncKey = "navGridLastSyncTs";
    const minIntervalMs = 10 * 60 * 1000;
    const metaDebounceMs = 30 * 1000;
    let metaRefreshTimer = null;
    let pendingRemoteStamp = 0;

    const readLastSyncAt = () => {
      if (typeof window === "undefined") return 0;
      try {
        return Number(window.localStorage.getItem(syncKey) || 0);
      } catch {
        return 0;
      }
    };

    const shouldSync = (force = false) => {
      if (force) return true;
      if (typeof window === "undefined") return true;
      return Date.now() - readLastSyncAt() > minIntervalMs;
    };

    const markSync = () => {
      if (typeof window === "undefined") return;
      try {
        window.localStorage.setItem(syncKey, String(Date.now()));
      } catch {}
    };

    let loadPromise = null;

    const fetchGrid = async (force = false) => {
      if (!active) return;
      try {
        const localGrid = loadNavGrid();
        const localHasSegments =
          Array.isArray(localGrid?.segments) && localGrid.segments.length > 0;
        if (localHasSegments && !shouldSync(force)) return;
        const res = await fetchNavGridFromCloud(db, localGrid, { force });
        const remote = res?.grid || null;
        if (!remote) return;
        if (applyRemoteNavGrid(remote, { force: force || res?.changed === true })) {
          window?.dispatchEvent?.(
            new CustomEvent("trekkin:navgrid-updated", { detail: { context: "sync" } })
          );
        }
        markSync();
      } catch (err) {
        console.warn("⚠️ Atualização da grelha falhou:", err);
      }
    };

    const loadOnDemand = (force = false, loadOptions = {}) => {
      if (loadPromise) return loadPromise;
      loadPromise = (async () => {
        await hydrateNavGridFromPersistent().catch(() => {});
        if (loadOptions?.localOnly !== true) {
          await fetchGrid(force);
        }
      })().finally(() => {
        loadPromise = null;
      });
      return loadPromise;
    };

    const handleRefresh = () => loadOnDemand(true);
    const handleToggleDemand = (event) => {
      if (event?.detail?.on !== true) return;
      loadOnDemand(false);
    };
    const handleDirectDemand = () => loadOnDemand(false);
    const handleOnline = () => loadOnDemand(false);
    const handleVisible = () => {
      if (document.visibilityState === "visible") loadOnDemand(false);
    };

    const scheduleRemoteMetaSync = (remoteStamp) => {
      pendingRemoteStamp = Math.max(pendingRemoteStamp, Number(remoteStamp || 0));
      if (metaRefreshTimer) window.clearTimeout(metaRefreshTimer);

      // Uma sessão de otimização pode atualizar dezenas de trechos. Sem esta
      // janela, cada alteração fazia todos os clientes descarregarem novamente
      // o índice completo da NavGrid. Agrupamos o lote e respeitamos o mesmo
      // intervalo usado pelas restantes sincronizações em background.
      const sinceLastSync = Date.now() - readLastSyncAt();
      const cooldownMs = Math.max(0, minIntervalMs - sinceLastSync);
      const delayMs = Math.max(metaDebounceMs, cooldownMs);
      metaRefreshTimer = window.setTimeout(() => {
        metaRefreshTimer = null;
        if (!active) return;
        const localStamp = Number(loadNavGrid()?.updatedAt || 0);
        if (pendingRemoteStamp <= localStamp) {
          pendingRemoteStamp = 0;
          return;
        }
        pendingRemoteStamp = 0;
        loadOnDemand(true);
      }, delayMs);
    };

    // O cache privado é a primeira fonte no arranque. Depois, se houver rede,
    // a comparação incremental com navGridMeta/navGridSegmentIndex descarrega
    // apenas os segmentos alterados.
    loadOnDemand(false);

    // navGridMeta é pequeno e funciona como versão remota da grelha. Assim que
    // o RTDB publicar uma versão mais recente, atualizamos o cache silenciosamente.
    const unsubscribeMeta = onValue(
      ref(db, "navGridMeta/updatedAt"),
      (snapshot) => {
        const remoteStamp = Number(snapshot.val() || 0);
        const localStamp = Number(loadNavGrid()?.updatedAt || 0);
        if (remoteStamp > localStamp) scheduleRemoteMetaSync(remoteStamp);
      },
      () => {}
    );

    if (typeof window !== "undefined") {
      window.__TREKKIN_LOAD_NAVGRID_ON_DEMAND = loadOnDemand;
      window.addEventListener("trekkin:navgrid-refresh", handleRefresh);
      window.addEventListener("trekkin:navgrid-toggle", handleToggleDemand);
      window.addEventListener("trekkin:navgrid-nodes-toggle", handleToggleDemand);
      window.addEventListener("trekkin:navgrid-edit-mode", handleToggleDemand);
      window.addEventListener("trekkin:navgrid-edit-nodes-toggle", handleToggleDemand);
      window.addEventListener("trekkin:navgrid-add-node-toggle", handleToggleDemand);
      window.addEventListener("trekkin:navgrid-delete-nodes-toggle", handleToggleDemand);
      window.addEventListener("trekkin:navgrid-split-node-toggle", handleToggleDemand);
      window.addEventListener("trekkin:navgrid-record-toggle", handleToggleDemand);
      window.addEventListener("trekkin:navgrid-demand", handleDirectDemand);
      window.addEventListener("trekkin:navgrid-focus-segment", handleDirectDemand);
      window.addEventListener("online", handleOnline);
      document.addEventListener("visibilitychange", handleVisible);
    }

    return () => {
      active = false;
      if (metaRefreshTimer) window.clearTimeout(metaRefreshTimer);
      if (typeof window !== "undefined") {
        if (window.__TREKKIN_LOAD_NAVGRID_ON_DEMAND === loadOnDemand) {
          delete window.__TREKKIN_LOAD_NAVGRID_ON_DEMAND;
        }
        window.removeEventListener("trekkin:navgrid-refresh", handleRefresh);
        window.removeEventListener("trekkin:navgrid-toggle", handleToggleDemand);
        window.removeEventListener("trekkin:navgrid-nodes-toggle", handleToggleDemand);
        window.removeEventListener("trekkin:navgrid-edit-mode", handleToggleDemand);
        window.removeEventListener("trekkin:navgrid-edit-nodes-toggle", handleToggleDemand);
        window.removeEventListener("trekkin:navgrid-add-node-toggle", handleToggleDemand);
        window.removeEventListener("trekkin:navgrid-delete-nodes-toggle", handleToggleDemand);
        window.removeEventListener("trekkin:navgrid-split-node-toggle", handleToggleDemand);
        window.removeEventListener("trekkin:navgrid-record-toggle", handleToggleDemand);
        window.removeEventListener("trekkin:navgrid-demand", handleDirectDemand);
        window.removeEventListener("trekkin:navgrid-focus-segment", handleDirectDemand);
        window.removeEventListener("online", handleOnline);
        document.removeEventListener("visibilitychange", handleVisible);
      }
      unsubscribeMeta();
    };
  }, [enabled, user?.uid, canSync]);
}
